import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsPunchOutSession,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
/* Custom code */
import { getCurrentCartState, isPunchOutOrder } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { StyledButton } from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { get, getProp } from "@insite/mobius/utilities";
import { siteWidthStyles } from "@tCentro/theme/mixins";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const isCartLoading = cartState.isLoading;
    const cart = cartState.value;
    const location = getLocation(state);

    return {
        siteName: state.context.website.name,
        stateContext: state.context,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        location,
        pageTitle: getCurrentPage(state).type,
        cart,
        isPunchOutSession: getIsPunchOutSession(state),
        isPunchOutOrder: isPunchOutOrder(cart),
        isCartLoading,
        rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
        settings: getSettingsCollection(state),
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    containerCustom?: InjectableCss;
    LogoContainer?: InjectableCss;
    LogoContainerNisco?: InjectableCss;
    middleWrapperNisco?: InjectableCss;
    leftSideSection?: InjectableCss;
    topRightWrapperLogin?: InjectableCss;
    topRow?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    leftWrapper?: InjectableCss;
    rightWrapper?: InjectableCss;
    rightWrapperNisco?: InjectableCss;
    middleWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    shopCentroContainer?: InjectableCss;
    mobileViewSearchBar?: InjectableCss;
    accountCartContainer?: InjectableCss;
    topHeaderRow?: InjectableCss;
    topHeaderRowNisco?: InjectableCss;
    topLeftWrapper?: InjectableCss;
    topRightWrapper?: InjectableCss;
    topRightWrapperNisco?: InjectableCss;
    topMiddleWrapper?: InjectableCss;
    middleHeaderRow?: InjectableCss;
    middleHeaderRowNisco?: InjectableCss;
    bottomHeaderRow?: InjectableCss;
    bottomRowleftWrapper?: InjectableCss;
    bottomRowrightWrapper?: InjectableCss;
    mainNavLinkListWrapper?: InjectableCss;
    wrapperContainer?: InjectableCss;
    leftWrapperNisco?: InjectableCss;
    bottomHeaderRowMobile?: InjectableCss;
    navNisco?: InjectableCss;
    middleHeaderRowLogo?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    middleHeaderRowLogo: {
        css: css`
            display: flex;
        `,
    },
    navNisco: {
        css: css`
            display: none !important;

            @media (min-width: 768px) and (max-width: 1078px) {
                display: block !important;
            }
        `,
    },

    mainNavLinkListWrapper: {
        css: css`
            display: flex;
            flex-basis: 100%;
            max-width: 100%;
            background-color: ${getColor("primary")};
            align-items: center;
            box-shadow: 0px 12px 24px rgba(26, 25, 25, 0.4);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background: none;
                        flex-basis: auto !important;
                        max-width: auto !important;
                        order: -1;
                    `,
                    css`
                        flex-basis: auto;
                        max-width: auto;
                        order: -1;
                    `,
                    css`
                        flex-basis: 4.65%;
                        max-width: 4.65%;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 7%;
                            max-width: 7%;
                            div {
                                &:nth-child(2) {
                                    display: none;
                                }
       
                        }
                    `,
                ])}
        `,
    },
    wrapperContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: right;
            gap:16px;
        `,
    },
    middleHeaderRowNisco: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 16px 62px 18px 0px;
            justify-content: center;
            /* position: fixed; */
            > div[data-test-selector="logoWrapper"] {
                > div [class^="Wrapper"] {
                    height: 100%;
                    div {
                        height: 100%;
                        div {
                            height: 100%;
                            a {
                                height: 100%;
                            }
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background-color: white;
                        justify-content: space-between;
                        padding-left: 16px;
                        padding-right: 16px;
                        padding-bottom: 16px;
                    `,
                    css`
                        background-color: white;
                        justify-content: space-between;
                        padding-left: 16px;
                        padding-right: 16px;
                        padding-bottom: 16px;
                    `,
                    css`
                        background-color: white;
                        flex-wrap: nowrap;
                    `,

                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            background-color: white;
                            flex-wrap: nowrap;
                        }
                    `,
                ])}
        `,
    },
    middleHeaderRow: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            /* position: fixed; */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background-color: ${get(theme, "colors.primary.main")};
                        justify-content: space-between;
                        padding-left: 16px;
                        padding-right: 16px;
                        padding-bottom: 16px;
                    `,
                    css`
                        background: ${get(theme, "colors.primary.main")};
                        justify-content: space-between;
                        padding-left: 16px;
                        padding-right: 16px;
                        padding-bottom: 16px;
                    `,
                    css`
                        background-color: ${get(theme, "colors.primary.main")};
                        flex-wrap: nowrap;
                    `,

                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            background-color: ${get(theme, "colors.primary.main")};
                            flex-wrap: nowrap;
                        }
                    `,
                ])}
        `,
    },
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            padding: 0 !important;
            max-width: none !important;
            position: fixed !important;
            z-index: 1280;
            .shipToHeader {
                background-color: rgb(241, 246, 251);
            }
        `,
    },

    container: {
        css: css`
            max-width: 1800px;
            width: 100%;
            height: auto;
            margin-right: auto;
            margin-left: auto;
        `,
    },
    LogoContainer: {
        css: css`
            max-width: 1800px;
            width: 100%;
            height: auto;
            margin-right: auto;
            margin-left: auto;
            border-bottom: 1px solid #747b83;
        `,
    },
    LogoContainerNisco: {
        css: css`
            max-width: 1800px;
            width: 100%;
            height: auto;
            margin-right: auto;
            margin-left: auto;
            border-bottom: 1px solid #747b83;
        `,
    },
    containerCustom: {
        css: css`
            max-width: 1800px;
            width: 100%;
            height: auto;
            margin-right: auto;
            margin-left: auto;
        `,
    },
    topRow: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: ${TYPOGRAPHY.size.tiny};
            padding-left: 15px;
            display: none;
            ~ header[class^="PageStyle"] {
                & + div[class^="PageStyle"] {
                    nav[data-test-selector="pageBreadcrumbs"] {
                        span[aria-current="page"] {
                            max-width: 400px;
                            white-space: nowrap;
                            text-overflow: ellipsis;
                            overflow: hidden;
                        }
                    }
                }
            }
        `,
    },
    leftWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 17%;
            max-width: 17%;
            /* position: relative; */
            padding-left: 64px;
            img {
                height: auto;
            }
            .wrapper {
                > a {
                    span {
                        width: 100%;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                        flex-basis: auto !important;
                        max-width: auto !important;
                        order: 2;
                        flex: 3;
                    `,
                    css`
                        padding-left: 0px;
                        flex-basis: auto !important;
                        max-width: auto !important;
                        order: 2;
                        flex: 3;
                    `,
                    css`
                        flex-basis: 15.12%;
                        max-width: 15.12%;
                        padding-left: 0px;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 15%;
                            max-width: 15%;
                            padding-left: 0px;
                        }
                    `,
                ])}
        `,
    },
    leftWrapperNisco: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 20%;
            max-width: 20%;
            padding-left: 64px;
            padding-right: 24px;
            img {
                max-height: 40px !important;
                max-width: 184px !important;
                object-fit: contain;
            }
            .wrapper {
                width: 100%;
                max-width: 184px;

                > a {
                    width: 100%;

                    span {
                        width: 100%;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                        flex-basis: auto !important;
                        max-width: auto !important;
                        order: 2;
                        flex: 3;
                        border: 2px solid white;
                        max-width: 60%;
                    `,
                    css`
                        padding-left: 0px;
                        flex-basis: auto !important;
                        max-width: auto !important;
                        order: 2;
                        flex: 3;
                        border: 2px solid white;
                        max-width: 60%;
                    `,
                    css`
                        flex-basis: 23.12%;
                        max-width: 23.12%;
                        padding-left: 16px;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 22%;
                            max-width: 22%;
                            padding-left: 64px;
                        }
                    `,
                ])}
        `,
    },
    middleWrapperNisco: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 72.6%;
            max-width: 72.6%;
            @media (min-width: 1080px) and (max-width: 1340px) {
                flex-basis: 70%;
                max-width: 70%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        order: 5;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        order: 5;
                    `,
                    css`
                        flex-basis: 80.51%;
                        max-width: 80.51%;
                        margin-left:10px;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 60%;
                            max-width: 60%;
                        }
                    `,
                ])}
        `,
    },
    middleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 55%;
            max-width: 55%;
            @media (min-width: 1080px) and (max-width: 1340px) {
                flex-basis: 50%;
                max-width: 50%;
                padding-left: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        order: 5;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        order: 5;
                    `,
                    css`
                        flex-basis: 68.51%;
                        max-width: 68.51%;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 60%;
                            max-width: 60%;
                        }
                    `,
                ])}
        `,
    },
    bottomHeaderRowMobile: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    rightWrapperNisco: {
        css: css`
            display: flex;
            white-space: nowrap;
            align-items: center;
            /* padding-left: 24px; */
            flex-basis: 25%;
            max-width: 25%;
            span {
                svg {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                stroke: ${getColor("primary")} !important;
                                fill: #2b5197 !important;
                            `,
                            css`
                                fill: #2b5197 !important;
                                stroke: ${getColor("primary")} !important;
                            `,
                            css`
                                fill: white;
                                border: 2px solid white;
                            `,
                            css`
                                stroke: ${getColor("primary")} !important;
                            `,
                        ])}
                    path {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    stroke: #2b5197 !important;
                                    fill: #2b5197 !important;
                                `,
                                css`
                                    fill: #2b5197 !important;
                                    stroke: #2b5197 !important;
                                `,
                                css``,
                            ])}
                    }
                }
            }
            > div[class^="Wrapper"]:first-child {
                //  margin-right: 49px;
            }
            > div {
                > a {
                    @media (min-width: 768px) {
                        display: none;
                    }
                }
                display: flex;
                justify-content: flex-end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            gap: 16px;
                        `,
                        css`
                            justify-content: center;
                            gap: 0px;
                            flex-basis: 17%;
                            max-width: 17%;
                        `,
                        css`
                            justify-content: center;
                        `,
                ])}
            }
            @media (min-width: 1080px) and (max-width: 1340px) {
                flex-basis: 25%;
                max-width: 25%;
                padding-left: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: auto !important;
                        max-width: auto !important;
                        padding-left: 0px;
                        order: 3;
                        flex: 1;
                    `,
                    css`
                        flex-basis: auto !important;
                        max-width: auto !important;
                        padding-left: 0px;
                        order: 3;
                    `,
                    css`
                        flex-basis: 11.62%;
                        max-width: 11.62%;
                        padding-left: 0px;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 12%;
                            max-width: 12%;
                            padding-left: 0px;
                        }
                    `,
                ])}
        `,
    },
    rightWrapper: {
        css: css`
            display: flex;
            white-space: nowrap;
            align-items: center;
            /* padding-left: 24px; */
            flex-basis: 24%;
            max-width: 24%;

            > div {
                > a {
                    @media (min-width: 768px) {
                        display: none;
                    }
                }
                display: flex;
                justify-content: flex-end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            gap: 16px;
                        `,
                        css`
                            justify-content: center;
                            gap: 16px;
                            flex-basis: 17%;
                            max-width: 17%;
                        `,
                        css`
                            justify-content: center;
                        `,
                    ])}
            }
            @media (min-width: 1080px) and (max-width: 1340px) {
                flex-basis: 28%;
                max-width: 28%;
                padding-left: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: auto !important;
                        max-width: auto !important;
                        padding-left: 0px;
                        order: 3;
                        flex: 1;
                    `,
                    css`
                        flex-basis: auto !important;
                        max-width: auto !important;
                        padding-left: 0px;
                        order: 3;
                    `,
                    css`
                        flex-basis: 11.62%;
                        max-width: 11.62%;
                        padding-left: 0px;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            flex-basis: 12%;
                            max-width: 12%;
                            padding-left: 0px;
                        }
                    `,
                ])}
        `,
    },
    mobileViewSearchBar: {
        css: css`
            margin-bottom: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 5px;
                    `,
                    null,
                    css`
                        margin-bottom: 5px;
                    `,
                    null,
                ])}
        `,
    },
    accountCartContainer: {
        css: css`
            display: flex;
            justify-content: space-around;
            align-items: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: inherit;
                    `,
                    css`
                        height: inherit;
                    `,
                    css`
                        height: inherit;
                    `,
                    css`
                        align-items: flex-start;
                    `,
                ])}
        `,
    },
    topHeaderRow: {
        css: css`
            display: flex;
            padding-top: 8px;
            padding-bottom: 8px;
            /* background: rgba(38, 107, 64, 0.04); */
            justify-content: flex-end;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            justify-content: space-between;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    topHeaderRowNisco: {
        css: css`
            display: flex;
            padding-top: 8px;
            padding-bottom: 8px;
            /* background: rgba(38, 107, 64, 0.04); */
            justify-content: space-between;
            margin-left: 64px;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            justify-content: space-between;
                            margin-left: 16px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    topLeftWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    topRightWrapperLogin: {
        css: css`
            display: flex;
            align-items: center;
            padding-right: 64px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        padding-right: 16px;
                    `,
                    css``,
                ])}
        `,
    },
    topRightWrapperNisco: {
        css: css`
            display: flex;
            align-items: center;

            a[data-test-selector="cartLink"] {
                padding: 8px 0px 8px 18px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css``,
                        css``,
                        css`
                            padding: 8px 0px 8px 18px !important;
                        `,
                        css``,
                    ])}
                span {
                    text-transform: uppercase !important;
                    font-family: Roboto Condensed;
                    font-weight: 700;
                    font-style: Bold;
                    font-size: 16px;
                    leading-trim: NONE;
                    line-height: 16px;
                    letter-spacing: 0%;
                    vertical-align: middle;
                    @media (min-width: 1080px) and (max-width: 1340px) {
                        font-size: 12px;
                        font-weight: 700;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        padding-right: 12px;
                    `,
                    css``,
                ])}
        `,
    },

    topRightWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding-right: 64px;
            //   padding-top: 31px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        padding-right: 12px;
                    `,
                    css``,
                ])}
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    location,
    pageTitle,
    cart,
    isPunchOutSession,
    isPunchOutOrder,
    isCartLoading,
    rfqMyQuotesPageUrl,
    stateContext,
    settings,
}: Props) => {
    const [siteNameheader, setheader] = useState(siteName);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {

        if (siteName !== "Nisco") {
            // Autopilot script URL (cookie)
            const scriptSrc = "//cdn.bc0a.com/autopilot/f00000000278287/autopilot_sdk.js";
    
            // Inject script on mount
            const script = document.createElement("script");
            script.src = scriptSrc;
            script.defer = true;
            document.head.appendChild(script);
    
            // Cleanup on unmount
            return () => {
                const scripts = document.querySelectorAll("script[src]");
                scripts.forEach((s: any) => {
                    if (s.src.includes("autopilot_sdk.js")) {
                        s.remove();
                    }
                });
            };
        }
    }, []);


    useEffect(() => {
        const header = document.querySelector('[data-test-selector="header"]');

        const updateHeaderHeight = () => {
            if (header) {
                const height = header.getBoundingClientRect().height;
                setHeaderHeight(height);
            }
        };

        updateHeaderHeight();

        window.addEventListener("resize", updateHeaderHeight);

        if (header) {
            const observer = new MutationObserver(updateHeaderHeight);
            observer.observe(header, { childList: true, subtree: true });
            return () => {
                observer.disconnect();
            };
        }

        return () => {
            window.removeEventListener("resize", updateHeaderHeight);
        };
    }, []);

    useEffect(() => {
        const main = document.getElementsByTagName("main");
        const navElement = document.querySelector('[data-test-selector="pageBreadcrumbs"]');
        if (headerHeight && main.length > 0 && navElement) {
            main[0].style.marginTop = `${headerHeight}px`;
        }
    }, [headerHeight]);
    const viewMyDashboardClickHandler = () => {
        if (session?.userRoles === "PunchOut" && rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        } else if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (location.pathname.includes("Invoices/Details")) {
            setheader(`${siteName} Invoice Share`);
        }
    }, [location]);

    useEffect(() => {
        if (
            session.hasRfqUpdates &&
            myAccountPageUrl &&
            session?.userRoles !== "PunchOut" &&
            (settings.customSettings as { enableRFQForPunchout?: boolean })?.enableRFQForPunchout
        ) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>
                            {session?.userRoles !== "PunchOut"
                                ? translate("View My Dashboard")
                                : translate("View My Quotes")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    if (session?.userRoles !== "PunchOut") {
                        dismissRfqUpdates();
                    }
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <Page
                data-test-selector="header"
                as="header"
                {...styles.page}
                isHeader={true}
                fullWidth={[true, true, true, true, true]}
            >
                {/* Commented code */
                /* <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                /> */}
                <EagerLoadingLogoProvider value={true}>
                    {/* <Zone contentId={id} zoneName="SecondaryNavigation" fixed /> */}
                    {siteName === "Nisco" ? (
                        <StyledWrapper>
                            <StyledWrapper
                                css={css`
                                    background-color: ${getColor("primary")};
                                `}
                            >
                                <StyledWrapper {...styles.containerCustom}>
                                    <StyledWrapper {...styles.topHeaderRowNisco}>
                                        <StyledWrapper {...styles.navNisco}>
                                            <Zone contentId={id} zoneName="navNiscoTablet"></Zone>
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.middleWrapperNisco}
                                            data-test-selector="SearchBarNisco"
                                        >
                                            <Zone contentId={id} zoneName="SearchBarNisco" />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.topLeftWrapper}>
                                            <Zone contentId={id} zoneName="shipToHeaderAddressNisco"></Zone>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.wrapperContainer}>
                                            <Hidden below="lg" {...styles.topRightWrapperNisco}>
                                                <Zone contentId={id} zoneName="mycartNisco"></Zone>
                                            </Hidden>
                                            <Hidden below="md" above="md" {...styles.topRightWrapperNisco}>
                                                <Zone contentId={id} zoneName="mycartNiscoTablet"></Zone>
                                            </Hidden>
                                            <Hidden below="lg" {...styles.topRightWrapperLogin}>
                                                <Zone contentId={id} zoneName="login/registerNisco"></Zone>
                                            </Hidden>
                                            <Hidden below="md" above="md" {...styles.topRightWrapperLogin}>
                                                <Zone contentId={id} zoneName="login/registerNiscoTablet"></Zone>
                                            </Hidden>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.bottomHeaderRowMobile}>
                                    <Zone contentId={id} zoneName="niscoMobile" />
                                </StyledWrapper>
                            </StyledWrapper>

                            <StyledWrapper {...styles.LogoContainerNisco}>
                              
                                <StyledWrapper {...styles.middleHeaderRowNisco}>
                                   
                                    <StyledWrapper
                                        css={css`
                                            flex-basis: 100%;
                                            max-width: 100%;
                                            display: flex;
                                            align-items: center;
                                            @media (min-width: 993px) and (max-width: 1079px) {
                                                justify-content: space-between;
                                            }
                                            ${({ theme }: { theme: BaseTheme }) =>
                                                breakpointMediaQueries(theme, [
                                                    css``,
                                                    css``,
                                                    css`
                                                        justify-content: space-between;
                                                    `,
                                                    null,
                                                ])}
                                        `}
                                    >
                                 
                                        <StyledWrapper {...styles.leftWrapperNisco} data-test-selector="logoWrapper">
                                            <Zone contentId={id} zoneName="LogoNisco" />
                                        </StyledWrapper>

                                        <StyledWrapper
                                            css={css`
                                                flex-basis: 55%;
                                                max-width: 55%;
                                                display: flex;
                                                align-items: center;
                                                @media (min-width: 768px) and (max-width: 1078px) {
                                                    display: none !important;
                                                }
                                                ${({ theme }: { theme: BaseTheme }) =>
                                                    breakpointMediaQueries(theme, [
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                        null,
                                                    ])}
                                            `}
                                        >
                                            <Zone contentId={id} zoneName="mainNavigationLinksNisco" />
                                        </StyledWrapper>
                                        <StyledWrapper
                                            css={css`
                                                flex-basis: 48%;
                                                max-width: 48%;
                                                display: flex;
                                                align-items: center;
                                                   @media (min-width: 1920px) and (max-width: 7680px) {

   display:none;
  
}
                                                ${({ theme }: { theme: BaseTheme }) =>
                                                    breakpointMediaQueries(theme, [
                                                        css`
                                                            max-width: 30%;
                                                            flex-basis: 30%;
                                                        `,
                                                        css`
                                                            max-width: 30%;
                                                            flex-basis: 30%;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                    ])}
                                            `}
                                        >
                                            <Zone contentId={id} zoneName="mainNavigationLinksNiscoMobile" />
                                        </StyledWrapper>
                                        <StyledWrapper
                                            css={css`
                                                flex-basis: 48%;
                                                max-width: 48%;
                                                display: flex;
                                                align-items: center;
                                                @media (min-width: 900px) and (max-width: 1078px) {
                                                    display: flex !important;
                                                    max-width: 70%;
                                                    flex-basis: 70%;
                                                }
                                                    @media (min-width: 1920px) and (max-width: 7680px) {

 display:none;
  
}
                                                ${({ theme }: { theme: BaseTheme }) =>
                                                    breakpointMediaQueries(theme, [
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            max-width: 60%;
                                                            flex-basis: 60%;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                    ])}
                                            `}
                                        >
                                            <Zone contentId={id} zoneName="mainNavigationLinksNiscoTab" />
                                        </StyledWrapper>

                                        <Hidden below="lg" {...styles.rightWrapperNisco} data-test-selector="MyAccount">
                                            <Zone contentId={id} zoneName="myAccountNisco" />
                                        </Hidden>
                                        <Hidden
                                            below="md"
                                            above="md"
                                            {...styles.rightWrapperNisco}
                                            data-test-selector="MyAccount"
                                        >
                                            <Zone contentId={id} zoneName="phoneTablet" />
                                        </Hidden>
                                        <Hidden
                                            above="sm"
                                            {...styles.rightWrapperNisco}
                                            data-test-selector="MyAccountMobile"
                                        >
                                            <Zone contentId={id} zoneName="cartMobileNisco" />
                                        </Hidden>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper>
                            <StyledWrapper
                                css={css`
                                    background: rgba(38, 107, 64, 0.04);
                                `}
                            >
                                <StyledWrapper {...styles.container}>
                                    <StyledWrapper {...styles.topHeaderRow}>
                                        <StyledWrapper {...styles.topLeftWrapper}>
                                            <Zone contentId={id} zoneName="shipToHeaderAddress"></Zone>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.topRightWrapper}>
                                            <Zone contentId={id} zoneName="mycart"></Zone>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.container}>
                                <StyledWrapper {...styles.middleHeaderRow}>
                                    <StyledWrapper {...styles.leftWrapper} data-test-selector="logoWrapper">
                                        <Zone contentId={id} zoneName="Logo" />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.middleWrapper} data-test-selector="SearchBar">
                                        <Zone contentId={id} zoneName="SearchBar" />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.rightWrapper} data-test-selector="MyAccount">
                                        <Zone contentId={id} zoneName="myAccount" />
                                    </StyledWrapper>
                                    <StyledWrapper
                                        data-test-selector="mainNavigation"
                                        {...styles.mainNavLinkListWrapper}
                                    >
                                        <StyledWrapper
                                            css={css`
                                                flex-basis: 70%;
                                                max-width: 70%;
                                            `}
                                        >
                                            <Zone contentId={id} zoneName="mainNavigationLinks" />
                                        </StyledWrapper>
                                        <StyledWrapper
                                            css={css`
                                                flex-basis: 30%;
                                                max-width: 30%;
                                                ${({ theme }: { theme: BaseTheme }) =>
                                                    breakpointMediaQueries(theme, [
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                        css`
                                                            display: none;
                                                        `,
                                                        null,
                                                    ])}
                                            `}
                                        >
                                            <Zone contentId={id} zoneName="LinkList" />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </EagerLoadingLogoProvider>
                <Zone contentId={id} zoneName="Breadcrumb" />
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
